import { Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import TableComponent from 'components/Table';
import React, { FC, useMemo } from 'react';
import { TFunction } from 'i18next';

import { ColumnsType, tableRowComponent } from '../services';
import { formatAttendeeRoles } from './ron-schedule-meeting/utils';

type AttendeesSectionType = {
  t: TFunction;
  tokens: tableRowComponent[];
  columns: ColumnsType;
  onCopyUrl: (url: string) => void;
};

const useStyles = makeStyles((theme: any) => ({
  tableStyle: {
    background: theme.palette.background.default,
    borderRadius: 8,
    overflow: 'hidden'
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  card: {
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.input.border}`,
    borderRadius: 8,
    padding: theme.spacing(2, 2, 1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0
  },
  value: {
    fontWeight: 500,
    overflowWrap: 'anywhere',
    userSelect: 'text'
  },
  roles: {
    display: 'block',
    color: theme.palette.text.secondary,
    overflowWrap: 'anywhere'
  },
  copyButton: {
    minHeight: theme.spacing(5),
    alignSelf: 'stretch'
  },
  copyText: {
    textTransform: 'none',
    fontWeight: 500
  }
}));

const AttendeesSection: FC<AttendeesSectionType> = ({
  t,
  tokens,
  columns,
  onCopyUrl
}) => {
  const classes = useStyles();
  const theme = useTheme();
  // Phone is < 600px (!up('sm')); noSsr avoids a first-paint card flash on wider viewports.
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const sortedTokens = useMemo(
    () => [...tokens].sort((a, b) => (
      (a.party_info?.name || '').localeCompare(b.party_info?.name || '')
      || (a.email || '').localeCompare(b.email || '')
    )),
    [tokens]
  );

  if (isSmUp) {
    return (
      <TableComponent
        disabledBorderLeft
        disableFullborderCell
        tableClassName={classes.tableStyle}
        columns={columns}
        dataSet={sortedTokens}
      />
    );
  }

  return (
    <ul className={classes.list}>
      {sortedTokens.map((token, index) => {
        const nameId = `ron-attendee-name-${index}`;
        const roles = formatAttendeeRoles(token.party_info?.kinds);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <li key={`${token.email}-${index}`}>
            <div role="group" aria-labelledby={nameId} className={classes.card}>
              <Typography id={nameId} variant="body2" className={classes.value}>
                {token?.party_info?.name || ''}
              </Typography>
              <Typography variant="body2" className={classes.value}>
                {token.email || t('ron:not-email')}
              </Typography>
              {roles && (
                <Typography variant="caption" className={classes.roles}>
                  {roles}
                </Typography>
              )}
              <Button
                className={classes.copyButton}
                aria-describedby={nameId}
                onClick={() => onCopyUrl(token.url)}
              >
                <Typography variant="body2" className={classes.copyText}>
                  {t('ron:copy-url')}
                </Typography>
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default AttendeesSection;
